import type { Product, ProductCompositionItem } from '@rosita/shared';

const DEFAULT_NEW_BADGE_EXPIRY_DAYS = 15;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface ProductBadges {
  isNew: boolean;
  isOutOfStock: boolean;
}

export function getProductBadges(
  product: Product,
  newBadgeExpiryDays: number = DEFAULT_NEW_BADGE_EXPIRY_DAYS
): ProductBadges {
  const created = new Date(product.createdAt).getTime();
  const cutoffMs = newBadgeExpiryDays * MS_PER_DAY;
  const isNew =
    product.highlightAsNew &&
    !Number.isNaN(created) &&
    Date.now() - created < cutoffMs;
  const isOutOfStock =
    product.variants.length > 0 && product.variants.every((v) => v.stock === 0);
  return { isNew, isOutOfStock };
}

// ─── Sizes ───────────────────────────────────────────────────────────
export const SIZE_ORDER = [
  'XXXS',
  'XXS',
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  'XXXL',
] as const;

/** Sort size labels: known letter sizes first in garment order, then the rest
 * alphabetically (numeric sizes like 36/38 fall in this second group). */
export function sortSizes(sizes: string[]): string[] {
  const known = SIZE_ORDER as readonly string[];
  return [...sizes].sort((a, b) => {
    const ai = known.indexOf(a);
    const bi = known.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.localeCompare(b, 'pt', { numeric: true });
  });
}

// ─── Composition & measurements ──────────────────────────────────────
/** "95% Algodão · 5% Elastano"; items without a percentage show the name only. */
export function formatComposition(items: ProductCompositionItem[]): string {
  return items
    .map((item) => (item.percentage != null ? `${item.percentage}% ${item.name}` : item.name))
    .join(' · ');
}

/** cm value with at most one decimal, pt-PT locale (comma separator). */
export function formatCm(value: number): string {
  return value.toLocaleString('pt-PT', { maximumFractionDigits: 1 });
}

/** A, B, C… label for the n-th measurement row (mirrors the Zara-style
 * lettered diagram convention without needing an actual sketch). */
export function measurementLetter(index: number): string {
  return String.fromCharCode(65 + (index % 26));
}
